import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button, Spinner } from '~/shared/ui';
import { useSession } from './SessionProvider';

/**
 * Sends unsigned visitors to the dedicated sign-in page instead of starting OAuth inline.
 */
export function RequireSession({ children }: { children: ReactNode }): JSX.Element {
  const { isSignedIn, isLoading } = useSession();
  const location = useLocation();
  const returnTo = `${location.pathname}${location.search}`;

  if (isLoading) {
    return (
      <div className="grid place-items-center py-24 text-slate-500">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }

  if (!isSignedIn) {
    return (
      <div className="panel mx-auto max-w-lg text-center">
        <p className="eyebrow">Authentication required</p>
        <h1 className="mt-3 font-display text-2xl font-bold text-slate-50">Sign in to continue</h1>
        <p className="mt-3 text-sm leading-relaxed text-slate-400">
          Installing, reviewing, and publishing need a Cloudflare account. You will see a clear
          Cloudflare button on the next screen.
        </p>
        <Link
          to={`/signin?returnTo=${encodeURIComponent(returnTo)}`}
          className="mt-6 inline-flex w-full"
        >
          <Button variant="primary" className="w-full">
            Go to sign in
          </Button>
        </Link>
      </div>
    );
  }

  return <>{children}</>;
}
